import { useState, useEffect } from 'react';
import { FiX, FiPlus, FiEdit2, FiTrash2, FiSave } from 'react-icons/fi';
import storageService from '../services/storageService';

/**
 * Modal para gestionar categorías de eventos (Tipo, Subtipo, Categoría)
 * Soporta CRUD completo con almacenamiento local
 */
export default function EventCategoryModal({ onClose, onSelect }) {
  const [categories, setCategories] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    tipo: 'tipo',
    nombre: '',
    color: '#3B82F6'
  });

  // Cargar categorías al montar
  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      await storageService.init();
      const cats = await storageService.eventCategories.obtenerTodos();
      setCategories(cats);
      console.log('📂 Categorías cargadas:', cats.length);
    } catch (error) {
      console.error('❌ Error cargando categorías:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingId) {
        // Actualizar categoría existente
        await storageService.eventCategories.actualizar(editingId, formData);
        console.log('✅ Categoría actualizada');
      } else {
        // Crear nueva categoría
        await storageService.eventCategories.agregar({
          ...formData,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        });
        console.log('✅ Categoría creada');
      }
      
      // Resetear formulario
      setFormData({ tipo: 'tipo', nombre: '', color: '#3B82F6' });
      setEditingId(null);
      setShowForm(false);
      
      // Recargar categorías
      await loadCategories();
    } catch (error) {
      console.error('❌ Error guardando categoría:', error);
      alert('Error al guardar la categoría');
    }
  };

  const handleEdit = (cat) => {
    setFormData({
      tipo: cat.tipo,
      nombre: cat.nombre,
      color: cat.color
    });
    setEditingId(cat.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('¿Estás seguro de eliminar esta categoría?')) return;
    
    try {
      await storageService.eventCategories.eliminar(id);
      console.log('✅ Categoría eliminada');
      await loadCategories();
    } catch (error) {
      console.error('❌ Error eliminando categoría:', error);
      alert('Error al eliminar la categoría');
    }
  };

  const handleSelectCategory = (cat) => {
    onSelect(cat);
    onClose();
  };

  // Agrupar categorías por tipo
  const categoriesByType = categories.reduce((acc, cat) => {
    if (!acc[cat.tipo]) acc[cat.tipo] = [];
    acc[cat.tipo].push(cat);
    return acc;
  }, {});

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold">📂 Gestionar Categorías</h2>
          <div className="flex gap-2">
            <button
              onClick={() => setShowForm(true)}
              className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <FiPlus className="w-5 h-5" />
              Nueva
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <FiX className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Formulario de creación/edición */}
          {showForm && (
            <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h3 className="text-lg font-semibold mb-4">
                {editingId ? '✏️ Editar Categoría' : '➕ Nueva Categoría'}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo *
                  </label>
                  <select
                    value={formData.tipo}
                    onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="tipo">Tipo</option>
                    <option value="subtipo">Subtipo</option>
                    <option value="categoria">Categoría</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre *
                  </label>
                  <input
                    type="text"
                    value={formData.nombre}
                    onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                    placeholder="Ej: Religioso, Personal, Familiar..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Color
                  </label>
                  <input
                    type="color"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    className="w-full h-10 rounded border border-gray-300"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowForm(false);
                      setEditingId(null);
                      setFormData({ tipo: 'tipo', nombre: '', color: '#3B82F6' });
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2"
                  >
                    <FiSave className="w-4 h-4" />
                    {editingId ? 'Actualizar' : 'Guardar'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Lista de categorías */}
          {categories.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <p className="text-lg mb-2">📭 No hay categorías</p>
              <p className="text-sm">Crea tu primera categoría haciendo clic en "Nueva"</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Tipos */}
              {categoriesByType.tipo && categoriesByType.tipo.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold mb-3 text-gray-800">🏷️ Tipos</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {categoriesByType.tipo.map(cat => (
                      <div
                        key={cat.id}
                        className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
                      >
                        <button
                          onClick={() => handleSelectCategory(cat)}
                          className="flex items-center gap-3 flex-1"
                        >
                          <div
                            className="w-4 h-4 rounded"
                            style={{ backgroundColor: cat.color }}
                          />
                          <span>{cat.nombre}</span>
                        </button>
                        <div className="flex gap-1">
                          <button
                            onClick={() => handleEdit(cat)}
                            className="p-1 hover:bg-blue-100 rounded"
                            title="Editar"
                          >
                            <FiEdit2 className="w-4 h-4 text-blue-600" />
                          </button>
                          <button
                            onClick={() => handleDelete(cat.id)}
                            className="p-1 hover:bg-red-100 rounded"
                            title="Eliminar"
                          >
                            <FiTrash2 className="w-4 h-4 text-red-600" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Subtipos */}
              {categoriesByType.subtipo && categoriesByType.subtipo.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold mb-3 text-gray-800">📌 Subtipos</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {categoriesByType.subtipo.map(cat => (
                      <div
                        key={cat.id}
                        className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
                      >
                        <button
                          onClick={() => handleSelectCategory(cat)}
                          className="flex items-center gap-3 flex-1"
                        >
                          <div
                            className="w-4 h-4 rounded"
                            style={{ backgroundColor: cat.color }}
                          />
                          <span>{cat.nombre}</span>
                        </button>
                        <div className="flex gap-1">
                          <button
                            onClick={() => handleEdit(cat)}
                            className="p-1 hover:bg-blue-100 rounded"
                            title="Editar"
                          >
                            <FiEdit2 className="w-4 h-4 text-blue-600" />
                          </button>
                          <button
                            onClick={() => handleDelete(cat.id)}
                            className="p-1 hover:bg-red-100 rounded"
                            title="Eliminar"
                          >
                            <FiTrash2 className="w-4 h-4 text-red-600" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Categorías */}
              {categoriesByType.categoria && categoriesByType.categoria.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold mb-3 text-gray-800">📂 Categorías</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {categoriesByType.categoria.map(cat => (
                      <div
                        key={cat.id}
                        className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
                      >
                        <button
                          onClick={() => handleSelectCategory(cat)}
                          className="flex items-center gap-3 flex-1"
                        >
                          <div
                            className="w-4 h-4 rounded"
                            style={{ backgroundColor: cat.color }}
                          />
                          <span>{cat.nombre}</span>
                        </button>
                        <div className="flex gap-1">
                          <button
                            onClick={() => handleEdit(cat)}
                            className="p-1 hover:bg-blue-100 rounded"
                            title="Editar"
                          >
                            <FiEdit2 className="w-4 h-4 text-blue-600" />
                          </button>
                          <button
                            onClick={() => handleDelete(cat.id)}
                            className="p-1 hover:bg-red-100 rounded"
                            title="Eliminar"
                          >
                            <FiTrash2 className="w-4 h-4 text-red-600" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}



